/**
* @author zjc[beica1@outook.com]
* @date 2021/12/14 15:19
* @description
*   Channel.common.vue of WeTrade
*/
<template>
<In use-footer>
  <t as="p" class="f-bold" path="deposit_3">
    Deposit Amount (US Dollar)
  </t>
  <HoldOn :content="options" :height="160">
    <Matrix :list="options" #="{ item }" class="mt-16">
      <ChoiceButton
        class="p-r"
        :class="{active: item === amount, 'disable': (item<channels[selectIndex].minAmount||item>channels[selectIndex].maxAmount)}"
        @click="changeDepositAmount(item)"
      >
        <money :v="item" />
      </ChoiceButton>
    </Matrix>
  </HoldOn>
  <ExchangeRate class="f-sm c-tip" :factor="amount" />
  <template v-if="showBonus">
    <Line class="mt-24" />
    <BonusList @select="select" :list="[10, 20, 30]" :selected="2" />
    <Line />
  </template>
  <div class="wedge mt-8" style="min-height: 64px">
    <div v-if="disabled"
      class="d-f p-8 br-lg"
      style="background: var(--color-asst-light);">
      <Pic height="16" width="16" src="trade/description2" class="mr-8" />
      <t path="deposit_31"
      :args="{min: '$' + channels[selectIndex].minAmount/format.moneyFactor}"
      class="c-asst f-md" style="line-height: 16px;"
      v-if="amount<channels[selectIndex].minAmount">
      Minimum deposit amount for current method is
       <money :v="channels[selectIndex].minAmount" />
      . Please choose another deposit method.
    </t>
    <t path="deposit_32"
      :args="{min: '$' + channels[selectIndex].maxAmount/format.moneyFactor}"
      class="c-asst f-md" style="line-height: 16px;"
      v-if="amount>channels[selectIndex].maxAmount">
      Maximum deposit amount for current method is
       <money :v="channels[selectIndex].maxAmount" />
      . Please choose another deposit method.
    </t>
    </div>
  </div>
  <template v-if="channel">
    <t as="p" path="deposit_15" class="f-bold"></t>
    <TheChannels :channels="channels" v-model:channel="channel" value-field="code"
      @change="channelChange"
      label-field="channel" />
    <div class="wedge" style="height: 56px"></div>
  </template>
  <div class="mt-16 f-md c-title" style="line-height: 20px;">
    <t as="div" path="deposit_6" multiline #="{td}" custom>
      <p v-for="(p, index) in td" :key="index">{{p}}</p>
    </t>
  </div>

  <template #footer>
    <Button class="click_to_pay f-bold f-lg br-lg" :class="{primary: !disabled}" @click="commit(amount)"
      :progress="progress">
      <t path="deposit_5" #="{td}" custom>{{td || 'Pay now'}}</t>
    </Button>
  </template>
</In>
</template>

<script lang="ts">
import Button from '@/components/Button.vue'
import ChoiceButton from '@/components/ChoiceButton.vue'
import { useDeposit } from '@/modules/in/in'
import { DepositChannel, readChannelList, readDepositChoices } from '@/modules/in/in.api'
import In from '@/modules/in/In.vue'
import TheChannels from '@/modules/out/components/TheChannels.vue'
import HoldOn from '@/provider/HoldOn.vue'
import ExchangeRate from '@/views/ExchangeRate.vue'
import Matrix from 'common/Matrix.vue'
import { defineComponent, shallowRef, nextTick, onMounted } from 'vue'
import Line from '@/components/Line.vue'
import BonusList from '@/modules/in/components/BonusList.vue'
import Pic from '@/components/Pic.vue'
import { format } from '@/config'

export default defineComponent({
  name: 'ChannelForCommon',
  components: {
    In,
    TheChannels,
    ChoiceButton,
    BonusList,
    Line,
    ExchangeRate,
    HoldOn,
    Button,
    Matrix,
    Pic,
  },
  props: {
    customRequest: Function,
  },
  setup (props) {
    const amount = shallowRef('0')
    const [commit, progress] = useDeposit()
    const channels = shallowRef<DepositChannel[]>()
    const channel = shallowRef<string>('')
    const options = shallowRef<Array<string>>([])
    const selectIndex = shallowRef(0)
    const disabled = shallowRef(false)

    onMounted(() => {
      Promise.all([readDepositChoices(), readChannelList()]).then((res) => {
        nextTick(() => {
          options.value = res[0].list
          channels.value = res[1]
          channel.value = res[1][0]?.code ?? ''
          for (let i = 0; i < options.value?.length; i++) {
            if (+options.value[i] >= +(channels.value?.[selectIndex.value].minAmount || 0)) {
              amount.value = options.value[i]
              break
            }
          }
          disableInit()
        })
      })
    })

    const disableInit = () => {
      const camount = amount.value === '0' ? +(options.value ?? ['0'])[0] : +amount.value
      const cmax = channels.value?.[selectIndex.value].maxAmount || +options.value[options.value.length - 1]
      const cmin = channels.value?.[selectIndex.value].minAmount || 0
      if (camount > cmax || camount < cmin) {
        disabled.value = true
      } else {
        disabled.value = false
      }
    }

    const changeDepositAmount = (item: string) => {
      amount.value = item
      disableInit()
    }

    const select = (data: string) => {
      console.log(data, '===')
    }

    const request = (amount: string) => {
      if (disabled.value) {
        return
      }
      const params: Record<string, unknown> = {
        amount,
      }

      if (channel.value) {
        params.payChannel = channel.value
      }

      const result = props.customRequest?.(params)

      if (result !== false) {
        return commit(params)
      }
    }

    const channelChange = (index: number) => {
      channel.value = channels.value ? channels.value[index].code : ''
      selectIndex.value = index
      disableInit()
    }

    return {
      channels,
      channel,
      options,
      amount,
      selectIndex,
      disabled,
      commit: request,
      progress,
      changeDepositAmount,
      select,
      channelChange,
      showBonus: false,
      format,
    }
  },
})
</script>

<style scoped lang="scss">
// .disable {
//   background: #DDE4EB;
//   color: var(--color-tip);

//   &.active {
//     border-color: var(--color-tip);
//     color: var(--color-title);

//     &::after {
//       background: var(--color-tip);
//     }
//   }
// }

.disable{
  background: #DDE4EB;
  color: var(--color-tip);
  &.active{
    border-color: var(--color-tip);
    color: var(--color-title);
    ::v-deep(.c-i) {
      background: var(--color-tip);
    }
  }
}

.choice {
  height: 40px;
  line-height: 32px;
  cursor: pointer;

  &.active {
    font-weight: bold;
  }
}
</style>
